/**
 * Main API file - Re-exports from modular structure
 *
 * Domain-specific modules:
 * - api/core.ts - Core request function and ApiError
 * - api/types.ts - All type definitions
 * - api/auth.ts - Authentication API
 * - api/projects.ts - Projects API
 * - api/models.ts - Models API
 * - api/tools.ts - Tools API
 * - api/prompts.ts - Prompts API
 * - api/agents.ts - Agents API
 * - api/environments.ts - Environments API
 * - api/simulations.ts - Simulations API
 * - api/admin.ts - Admin API
 * - api/connectors.ts - Connectors API
 * - api/iam.ts - IAM API
 * - api/dashboard.ts - Dashboard API
 * - api/health.ts - Health API
 * - api/skills.ts - Skills API
 * - api/dependencies.ts - Dependencies API
 * - api/platformAdmin.ts - Platform Admin API
 * - api/rag.ts - RAG API
 * - api/memory.ts - Memory API
 * - api/uniqueness.ts - Uniqueness API
 * - api/monitoring.ts - Monitoring API
 */

// Re-export everything from the modular structure
export * from './api'

// Explicit exports
export { ApiError, request, isTokenExpiringSoon, refreshAccessToken, type RequestOptions } from './api/core'
export * from './api/types'
export { authApi } from './api/auth'
export { dashboardApi } from './api/dashboard'
export { healthApi } from './api/health'
export {
  promptsApi,
  isPlatformPromptId,
  isCanonicalPromptId,
  mapPromptRefToId,
  PROMPT_VERSIONS_PAGE_SIZE,
  mergePromptVersions,
  promptDetailToVersion,
} from './api/prompts'
export { projectsApi } from './api/projects'
export { modelsApi } from './api/models'
export { toolsApi, type ToolTemplate, type ToolCategory } from './api/tools'
export { agentsApi } from './api/agents'
export { aiBlueprintsApi } from './api/aiBlueprints'
export { externalAgentsApi } from './api/externalAgents'
export { environmentsApi } from './api/environments'
export {
  rlEnvironmentsApi,
  type RLEnvironment,
  type RLEnvironmentCreatePayload,
} from './api/rlEnvironments'
export { simulationsApi } from './api/simulations'
export { adminApi } from './api/admin'
export { connectorsApi } from './api/connectors'
export { benchmarkStudioApi } from './api/benchmarkStudio'
export type {
  BenchmarkJob,
  BenchmarkJobStatus,
  JobList as BenchmarkJobList,
  JobCreatePayload as BenchmarkJobCreatePayload,
  JobUpdatePayload as BenchmarkJobUpdatePayload,
  BenchmarkTask,
  TaskList as BenchmarkTaskList,
  TaskIngestRow as BenchmarkTaskIngestRow,
  TaskIngestResponse as BenchmarkTaskIngestResponse,
  BenchmarkAnnotation,
  AnnotationUpsertPayload as BenchmarkAnnotationUpsertPayload,
  AssignmentCreatePayload as BenchmarkAssignmentCreatePayload,
  AssignmentRead as BenchmarkAssignmentRead,
  TaskDetail as BenchmarkTaskDetail,
  SnapshotRead as BenchmarkSnapshotRead,
  RubricRead as BenchmarkRubricRead,
  RubricCreatePayload as BenchmarkRubricCreatePayload,
  RubricSchema as BenchmarkRubricSchema,
  RubricCriterion as BenchmarkRubricCriterion,
  JobMetrics as BenchmarkJobMetrics,
  ConversationTurn,
  ConversationTurnsResponse,
  BenchmarkSnapshotDiff,
  ExternalInviteRead,
  ExternalInviteIssued,
  PayoutRead,
  CalibrationRead,
  InterviewQueueItem,
  InterviewQueueResponse,
  InterviewTurnPayload,
  InterviewAction,
  InterviewClientAction,
  InterviewTranscriptMessage,
} from './api/benchmarkStudio'
export {
  graphPreviewSettingsApi,
  type GraphPreviewEffectiveRead,
  type GraphPreviewTenantAdminRead,
  type GraphPreviewTenantAdminUpdate,
} from './api/graphPreviewSettings'
export { iamApi } from './api/iam'
export { skillsApi, agentSkillsApi } from './api/skills'
export { dependenciesApi, bulkCheckByTenant } from './api/dependencies'
export { platformAdminApi } from './api/platformAdmin'
export { ragApi } from './api/rag'
export type {
  QueryAnalysisRequest,
  QueryAnalysisResponse,
  RetrieveRequest,
  RetrieveResponse,
  RetrievalResultItem,
  QualityMetrics,
  Citation,
  CitedResponse,
  StrategyInfo,
  StrategyStats,
  QualityTrendPoint,
  RecentRetrieval,
  CostSummary,
} from './api/rag'
export { memoryApi } from './api/memory'
export type {
  MemoryStats,
  MemoryOperation,
  OperationsResponse,
  RetrievalScore,
  RetrievalScoresResponse,
  MemoryContextResponse,
  MemoryRecord,
  MemoryRecordList,
  FewShotExampleType,
  FewShotExample,
  FewShotExampleCreate,
  FewShotExampleUpdate,
  FewShotQuality,
} from './api/memory'
export { uniquenessApi } from './api/uniqueness'
export type { EntityType, UniquenessCheckParams, UniquenessCheckResponse } from './api/uniqueness'
export { monitoringApi } from './api/monitoring'
export type {
  ObservabilityToolInfo,
  ObservabilityUrlsResponse,
} from './api/monitoring'
export { deploymentsApi } from './api/deployments'
export { agentEvalsApi } from './api/agentEvals'
export type {
  EvalSession,
  EvalRowResult,
  AgentSummaryItem,
  AgentEvalsSummary,
  AgentPortfolioEntry,
  MetricCatalogEntry,
  MetricCatalogResponse,
  EvalTemplate,
  CreateSessionPayload,
  CreateTemplatePayload,
  UpdateTemplatePayload,
} from './api/agentEvals'
export { optimizationApi } from './api/optimization'
export type {
  OptimizationConfig,
  OptimizationSessionResponse,
  CreateOptimizationRequest,
  OptimizerRegistryMeta,
} from './api/optimization'
export { arenaApi } from './api/arena'
export type {
  ArenaSTTResponse,
  ArenaTTSResponse,
  ArenaRerankResponse,
  ArenaImageResponse,
} from './api/arena'
